import { useAuth } from '@/contexts/AuthContext';
import { canAccessRoute, hasPermission, getDefaultLandingPage, AppRole, ROLE_PERMISSIONS } from '@/lib/permissions';

/**
 * Hook to check user permissions
 */
export function usePermissions() {
  const { userRoles } = useAuth();

  const currentRoles = (userRoles?.map(r => r.role) || []) as AppRole[];

  return {
    /**
     * Check if user can access a specific route
     */
    canAccess: (route: string) => canAccessRoute(currentRoles, route),

    /**
     * Check if user has a specific permission
     */
    can: (permission: keyof typeof ROLE_PERMISSIONS) => hasPermission(currentRoles, permission),

    /**
     * Get the default landing page for the user
     */
    getDefaultPage: () => getDefaultLandingPage(currentRoles),

    /**
     * Check if user has a specific role
     */
    hasRole: (role: AppRole) => currentRoles.includes(role),

    /**
     * Check if user has any of the specified roles
     */
    hasAnyRole: (roles: AppRole[]) => roles.some(role => currentRoles.includes(role)),

    /**
     * Get all current user roles
     */
    roles: currentRoles,

    /**
     * Check if user is super admin or admin
     */
    isFullAdmin: currentRoles.includes('super_admin') || currentRoles.includes('admin'),
  };
}
